import { useEffect, useMemo, useState, type MouseEvent } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationType, type Notification } from '../../types/notification';

const notificationTypeIconMap: Record<number, string> = {
  [NotificationType.IncidentCreated]: '🆕',
  [NotificationType.IncidentAssigned]: '👤',
  [NotificationType.IncidentTeamAssigned]: '👥',
  [NotificationType.IncidentStatusChanged]: '🔄',
  [NotificationType.IncidentResolved]: '✅',
  [NotificationType.IncidentEscalated]: '⚠️',
  [NotificationType.IncidentSlaBreached]: '🚨',
  [NotificationType.IncidentSlaWarning]: '⏳',
  [NotificationType.IncidentPriorityChanged]: '📌',
  [NotificationType.IncidentMajorIncidentChanged]: '🔥',
  [NotificationType.IncidentCommentAdded]: '💬',
  [NotificationType.DiscoveryJobStarted]: '🔄',
  [NotificationType.DiscoveryJobCompleted]: '✅',
  [NotificationType.DiscoveryJobFailed]: '❌',
  [NotificationType.DiscoveryJobCancelled]: '⏹️',
  [NotificationType.OnCallAlert]: '📟',
  [NotificationType.EscalationAlert]: '🚨',
  [NotificationType.OnCallShiftStarting]: '🔔',
  [NotificationType.OnCallShiftEnding]: '🔕',
  [NotificationType.AgreementReviewOverdue]: '📅',
  [NotificationType.AiBudgetThresholdReached]: '💳',
};

interface DiscoveryNotificationMetadata {
  discoverySourceId?: string;
  sourceName?: string;
  status?: string;
}

function parseDiscoveryMetadata(metadata: string | null): DiscoveryNotificationMetadata {
  if (!metadata) {
    return {};
  }

  try {
    const parsed = JSON.parse(metadata) as DiscoveryNotificationMetadata;
    return parsed ?? {};
  } catch {
    return {};
  }
}

function getNotificationPath(notification: Notification): string | null {
  if (notification.sourceEntityType === 'Incident') {
    return `/incidents/${notification.sourceEntityId}`;
  }

  if (notification.sourceEntityType === 'DiscoveryJob') {
    const metadata = parseDiscoveryMetadata(notification.metadata);
    if (metadata.discoverySourceId) {
      return `/admin/integrations/discovery-jobs/${metadata.discoverySourceId}/${notification.sourceEntityId}`;
    }

    return '/admin/integrations/discovery-jobs';
  }

  if (notification.sourceEntityType === 'Agreement') {
    return `/admin/itil-configuration?tab=agreements&agreementId=${notification.sourceEntityId}`;
  }

  return null;
}

function renderDiscoveryStatus(type: number) {
  if (type === NotificationType.DiscoveryJobStarted) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
        <svg className="h-3 w-3 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
        </svg>
        In Progress
      </span>
    );
  }

  if (type === NotificationType.DiscoveryJobCompleted) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-300">
        ✓ Completed
      </span>
    );
  }

  if (type === NotificationType.DiscoveryJobFailed) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[11px] font-medium text-red-700 dark:bg-red-900/30 dark:text-red-300">
        ✕ Failed
      </span>
    );
  }

  if (type === NotificationType.DiscoveryJobCancelled) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-gray-200 px-2 py-0.5 text-[11px] font-medium text-gray-700 dark:bg-gray-700 dark:text-gray-200">
        ■ Cancelled
      </span>
    );
  }

  return null;
}

export function NotificationPanel() {
  const navigate = useNavigate();
  const [isDismissAllDialogOpen, setIsDismissAllDialogOpen] = useState(false);
  const {
    isOpen,
    isLoading,
    notifications,
    unreadCount,
    closePanel,
    markAsRead,
    markAllAsRead,
    clearAll,
    deleteNotification,
  } = useNotifications();

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        closePanel();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closePanel]);

  const sortedNotifications = useMemo(() => {
    return [...notifications].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );
  }, [notifications]);

  const handleNotificationClick = async (notification: Notification) => {
    const targetPath = getNotificationPath(notification);
    if (targetPath) {
      navigate(targetPath);
    }
    await markAsRead(notification.id);
    closePanel();
  };

  const handleDelete = async (event: MouseEvent<HTMLButtonElement>, notificationId: string) => {
    event.stopPropagation();
    await deleteNotification(notificationId);
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-black/30 transition-opacity ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closePanel}
        aria-hidden="true"
      />

      <aside
        className={`fixed top-0 right-0 z-50 h-full w-full sm:w-[420px] bg-white dark:bg-gray-800 shadow-2xl border-l border-gray-200 dark:border-gray-700 transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-label="Notifications panel"
      >
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Notifications</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => void markAllAsRead()}
                disabled={unreadCount === 0}
                className="text-sm px-3 py-1.5 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-primary-900/30 dark:text-primary-300 dark:hover:bg-primary-900/50"
              >
                Mark all read
              </button>
              <button
                type="button"
                onClick={() => setIsDismissAllDialogOpen(true)}
                disabled={notifications.length === 0}
                className="text-sm px-3 py-1.5 rounded-md bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50"
              >
                Clear all
              </button>
              <button
                type="button"
                onClick={closePanel}
                className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700"
                aria-label="Close notifications"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">Loading notifications...</div>
            ) : sortedNotifications.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400">No notifications yet.</div>
            ) : (
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortedNotifications.map((notification) => (
                  <li key={notification.id}>
                    <div
                      className={`w-full text-left px-4 py-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                        notification.isRead
                          ? 'bg-white dark:bg-gray-800 border-l-4 border-transparent'
                          : 'bg-primary-50/60 dark:bg-primary-900/20 border-l-4 border-primary-500'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => void handleNotificationClick(notification)}
                          className="min-w-0 flex-1 text-left"
                        >
                          <div className="flex items-start gap-2">
                            <span className="text-base leading-6" aria-hidden="true">
                              {notificationTypeIconMap[notification.type] ?? '🔔'}
                            </span>
                            <div className="min-w-0">
                              <p
                                className={`text-sm text-gray-900 dark:text-gray-100 truncate ${
                                  notification.isRead ? 'font-semibold' : 'font-bold'
                                }`}
                              >
                                {notification.title}
                              </p>
                              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 break-words">
                                {notification.message}
                              </p>
                              {renderDiscoveryStatus(notification.type)}
                              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                                {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true })}
                              </p>
                            </div>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={(event) => void handleDelete(event, notification.id)}
                          className="p-1 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex-shrink-0"
                          aria-label="Delete notification"
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 7h12M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2m-7 0h8m-1 0v11a2 2 0 01-2 2H10a2 2 0 01-2-2V7h8z"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </aside>

      <ConfirmDialog
        isOpen={isDismissAllDialogOpen}
        title="Dismiss all notifications"
        message="Dismiss all notifications from this panel? They will still be available in the Notification Center."
        confirmLabel="Dismiss all"
        variant="danger"
        onCancel={() => setIsDismissAllDialogOpen(false)}
        onConfirm={() => {
          void clearAll();
          setIsDismissAllDialogOpen(false);
        }}
      />
    </>
  );
}
